import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { sortWith, compose, pipe, descend, prop, map } from "ramda";

import { adEngagePath } from "utils/constants";

import AdEngageButton from "components/ad_engage_button";
import SideBar, { IMenuItem } from "../sidebar";
import BrandSideBarMenu from "../brand_sidebar_menu";
import { LoadingIndicatorInline } from "../loading_indicator";
import ProgressBar from "../progress_bar";

import * as styles from "./styles.module.scss";

import { BrandData, CreativeMetadata } from "models/brand";
import Paginator from "components/paginator/index";
import CreativeCard from "components/creative_card/index";
import ToplineSummary from "components/topline_summary/index";
import ShareOfPlatformOverview from "../share_of_platform_overview";
import MoreBrandsMoreCategoriesModal from "../more_brands_more_categories";

export interface BrandOverviewProps {
  category?: { categoryId: string, categoryName: string };
  brand?: { brandId: string, brandName: string };
  detail?: string;
  fromTopBrands?: boolean;
  brandData?: BrandData;
  categoryData?: any;
  pageLimit?: number;
  loading?: boolean;
  children?: React.ReactNode;
}

interface Brand {
  id?: any;
  name?: string;
  url?: string;
  linear?: number;
  hulu?: number;
}

const BrandOverview: React.FC<BrandOverviewProps> = (props: BrandOverviewProps) => {
  const { category, brand, detail, fromTopBrands, brandData, categoryData, pageLimit = 12, loading = false } = props;

  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);

  // brand selectedDetail based on path
  const selectedMenuItem = detail === "share-of-platform" ? "Share of Platform" : "Creatives";
  const brandUrl = adEngagePath + `/${category.categoryId}_${category.categoryName}/${brand.brandId}_${brand.brandName}`;

  const generateShareofPlatformRows = () => {
    if (!categoryData || !brandData) { return [] }

    const brand = {
      id: brandData.brand_data.brand_id,
      name: brandData.brand_data.brand_name,
      url: brandData.brand_data.image_url,
      linear: brandData.platform_share.linear,
      hulu: brandData.platform_share.hulu
    }

    // get top ten
    const brandTopTen = map(
      // mapping data from array to object
      ([id, name, url, linear, hulu]) => ({ id, name, url, linear, hulu })
    )(categoryData.platform_share_top_ten)

    // if current brand is not in top ten, add it at the bottom

    if (!brandTopTen.some(cur => cur.id === brand.id)) {
      brandTopTen.push(brand);
    }


    // generate rows
    const toRows = pipe(
      sortWith([compose(descend<Brand>(prop('hulu')))]),
      map(({ id, name, url, linear, hulu }) => ({
        id,
        name,
        linear: `${linear}%`,
        hulu: `${hulu}%`,
        isHighlighted: id === brand.id,
      }))
    );

    return toRows(brandTopTen)
  }

  let sidebarMenuItems: Array<IMenuItem> = [
    {
      divider: true
    },
    {
      label: "Creatives",
      locked: false,
      path: brandUrl,
    },
    {
      label: "Share of Platform",
      locked: false,
      path: brandUrl + "/share-of-platform",
    },
    {
      label: "Summary",
      locked: true,
    },
    {
      label: "Products",
      locked: true,
    },
    {
      label: "Networks",
      locked: true,
    },
    {
      label: "Programs",
      locked: true,
    },
    {
      label: "Dayparts",
      locked: true,
    },
    {
      label: "Durations",
      locked: true,
    },
    {
      label: "Airings",
      locked: true,
    },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [brand, detail, category]);

  const emptyState = !loading && (!brandData?.creative_metrics?.length || brandData?.creative_metrics?.length === 0);
  const content = !emptyState && brandData?.creative_metrics.length > 0
    ? (<>
      {brandData?.creative_metrics?.slice((currentPage - 1) * pageLimit, currentPage * pageLimit).map((entity: CreativeMetadata, i: number) => {
        return <CreativeCard
          key={i}
          metrics={entity}
          brandMetadata={brandData?.brand_data}
        />;
      })
      }
      <div className={styles.brandOverviewPaginatorContainer}>
        <Paginator
          extraClassNames={styles.brandOverviewPaginator}
          totalCount={brandData?.creative_metrics ? brandData?.creative_metrics?.length : 0}
          limitPerPage={pageLimit}
          onPageNumberChange={(newPageNumber: number) => { setCurrentPage(newPageNumber) }}
          currentPage={currentPage}
        />
      </div>
    </>)
    : (<div className={classNames("flex-row", styles.emptyStateContainer)}>
      <div className={classNames("flex-col", styles.emptyStateHeader)}>
        <div className={styles.emptyStateTitle}>
          No recent airings above our threshold, contact us
        </div>
        <div className={styles.emptyStateSubtitle}>
          EDO monitors 120+ national broadcast and cable TV networks to continuously collect and maintain data for all national TV ads going back to January 2015.
        </div>
      </div>
      <AdEngageButton label="Request Demo" gotoLink="/contact-us" href={"/contact-us"} extraClassNames={styles.emptyStateDemoButton} />
    </div>)
    ;

  const renderContent = () => {
    if (detail === "share-of-platform") {
      return (
        <div className={classNames("flex-fill-remainder has-background-anti-flash-white full-height-and-width-minus-navbar-and-sidebar", styles.brandOverview)}>
          <ShareOfPlatformOverview type="Brand" rows={generateShareofPlatformRows()} onModalClick={() => setShowModal(true)} />
        </div>
      )
    }

    return (
      <div className={classNames("flex-fill-remainder has-background-anti-flash-white full-height-and-width-minus-navbar-and-sidebar", styles.brandOverview)}>
        <ToplineSummary airings={brandData?.brand_data?.tv_ad_airings_count} />
        <div className={classNames("flex-row flex-wrap", styles.creativesContainer)}>
          <div className={classNames("flex-row", styles.creativeHeaderContainer)}>
            <div className={"flex-col"}>
              <div className={styles.creativeTitle}>
                Most Aired Creatives
              </div>
              <div className={styles.creativeSubtitle}>
                Last 90 Days
                {!emptyState ? " • Sorted By Airings" : ""}
              </div>
            </div>
            <div className={classNames("flex-col", styles.lastUpdated)}>
              {`Last Updated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
            </div>
          </div>

          {loading
            ? <div className={classNames("flex-col fill-parent", styles.loadingContainer)}>
              <ProgressBar loading={loading} />
              <div className={classNames("flex-fill-remainder fill-parent", styles.loadingSpinnerContainer)}>
                <LoadingIndicatorInline width="64px" height="64px" />
              </div>
            </div>
            : content
          }
          {props?.children}
        </div>
        <div className={styles.creativesBottomMargin} />
      </div>
    )
  }

  return (
    <div className="fill-parent flex-row has-background-anti-flash-white">
      <MoreBrandsMoreCategoriesModal open={showModal} onClose={() => setShowModal(false)} />
      <SideBar extraClassNames={styles.brandSidebar}>
        <BrandSideBarMenu
          selectedItem={selectedMenuItem}
          menuItems={sidebarMenuItems}
          fromTopBrands={fromTopBrands}
          brandMetadata={brandData?.brand_data} />
      </SideBar>
      {renderContent()}
    </div>
  );
};

export default BrandOverview;
